import {
    Badge,
    Box,
    Button,
    Flex,
    HStack,
    Icon,
    Text,
    VStack,
    useToast,
} from '@chakra-ui/react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { TbInfoCircle } from 'react-icons/tb';
import type { QueryObserverResult, RefetchOptions } from '@tanstack/react-query';
import type { OrgVendor, SettingsResponse } from '../../../../hooks/useGetSettings';
import { useGetLocations } from '../../../../hooks/useGetLocations';
import { useUpdateSettings } from '../../../../hooks/useUpdateSettings';
import type { SupplierSearchResult } from '../../../../hooks/useSearchSuppliers';
import {
    SettingsCard,
    SettingsFlow,
    SettingsFlowSplit,
    SettingsRow,
    useSettingsDetailCrumbs,
} from '../../../../components/settings/primitives';
import { SegmentedControl } from '../../../../ui';
import ProhibitedVendorAlertDialog from './ProhibitedVendorAlertDialog';
import VendorPreferencesTable from './VendorPreferencesTable';
import VendorSearchField from './VendorSearchField';
import { VendorMakesSelect, VendorPercentInput } from './VendorFields';
import {
    buildVendorPreferences,
    toLocationPayload,
    type VendorPreferences,
} from './vendorPreferences';

export interface VendorDetailSectionProps {
    /**
     * The vendor's index in the vendors array, or 'new' for the add case. One
     * supplier can hold several rows — the same vendor with different makes —
     * so the supplier id does not identify a row.
     */
    vendorId: string;
    vendors: Array<OrgVendor>;
    oemOptions: SettingsResponse['oemOptions'];
    refetch: (options?: RefetchOptions) => Promise<QueryObserverResult<any, Error>>;
}

interface VendorDraft {
    supplierId: string;
    name: string;
    formattedAddress?: string;
    /** Kept as text so a half-typed value survives (e.g. "1" before "10"). */
    discount: string;
    /** null is none: this vendor is not a direct-ordering supplier. */
    directOrdering: 'opstrax' | 'collisionlink' | null;
    isDealer: boolean;
    /** Make ids, or 'all'. Dealers only — everyone else saves as 'all'. */
    makes: Array<string> | 'all';
    rebate: string;
    nonDrpFees: string;
    preferences: VendorPreferences;
}

type MakeOption = { _id: string; name: string; };

type DirectOrderingChoice = 'none' | 'opstrax' | 'collisionlink';

const DIRECT_ORDERING_OPTIONS: Array<{ value: DirectOrderingChoice; label: string }> = [
    { value: 'none', label: 'None' },
    { value: 'opstrax', label: 'OPSTrax' },
    { value: 'collisionlink', label: 'CollisionLink' },
];

const percentToText = (value?: number): string =>
    value == null ? '' : `${Math.round(value * 10000) / 100}`;

const textToPercent = (value: string): number | undefined => {
    const parsed = parseFloat(value);
    return value.trim() && Number.isFinite(parsed) ? parsed / 100 : undefined;
};

const toMakesDraft = (vendor: OrgVendor | undefined): Array<string> | 'all' => {
    if (!vendor?.isDealer || !vendor.makes || vendor.makes === 'all') return 'all';
    return vendor.makes.map((make) => make._id);
};

const toMakesPayload = (
    value: Array<string> | 'all',
    oemOptions: Array<MakeOption>,
): OrgVendor['makes'] => {
    if (value === 'all') return 'all';
    return value
        .map((id) => oemOptions.find((option) => option._id === id))
        .filter((option): option is MakeOption => !!option)
        .map((option) => ({ _id: option._id, name: option.name }));
};

const getMakeIds = (value: OrgVendor['makes'] | Array<string> | 'all'): 'all' | string[] => {
    if (value === 'all') return 'all';
    return value.map((item) => (typeof item === 'string' ? item : item._id));
};

const makesOverlap = (
    a: OrgVendor['makes'] | Array<string> | 'all',
    b: OrgVendor['makes'] | Array<string> | 'all',
): boolean => {
    const aIds = getMakeIds(a);
    const bIds = getMakeIds(b);
    if (aIds === 'all' || bIds === 'all') return true;
    const other = new Set(bIds);
    return aIds.some((id) => other.has(id));
};

const DEALER_ONLY_PREFERENCES = ['required', 'superPreferred'] as const;

const withoutDealerPreferences = (preferences: VendorPreferences): VendorPreferences => ({
    ...preferences,
    required: [],
    superPreferred: [],
});

const buildDraft = (
    vendor: OrgVendor | undefined,
    locations: Array<{ _id: string | 'all'; title: string; }>,
): VendorDraft => {
    const isDealer = vendor?.isDealer ?? false;
    const preferences = buildVendorPreferences(vendor, locations);
    return {
        supplierId: vendor?.supplierId ?? '',
        name: vendor?.name ?? '',
        formattedAddress: vendor?.formattedAddress,
        discount: percentToText(vendor?.discount),
        directOrdering: vendor?.directOrdering ?? null,
        isDealer,
        makes: toMakesDraft(vendor),
        rebate: percentToText(vendor?.marginBump),
        nonDrpFees: percentToText(vendor?.ptFee),
        preferences: isDealer ? preferences : withoutDealerPreferences(preferences),
    };
};

/**
 * Add or edit one vendor, full screen: the vendor and its terms on the left,
 * preferences by location on the right. Replaces the two-step vendor modal —
 * the same screen serves both cases, so editing a vendor and adding one look
 * and behave the same, and the location table gets the height it needs.
 *
 * Saving writes the whole vendors array through POST /settings/update, which is
 * how this endpoint works, so the draft is merged into the current list here.
 */
const VendorDetailSection = ({
    vendorId,
    vendors,
    oemOptions,
    refetch,
}: VendorDetailSectionProps) => {
    const navigate = useNavigate();
    const toast = useToast();
    const updateSettings = useUpdateSettings();
    const { data: locationsData } = useGetLocations();
    const prohibitedConfirmRef = useRef<HTMLButtonElement>(null);
    const [showProhibitedPopup, setShowProhibitedPopup] = useState(false);

    const isAddMode = vendorId === 'new';
    const vendorIndex = isAddMode ? -1 : Number(vendorId);
    const vendor =
        !isAddMode && Number.isInteger(vendorIndex) ? vendors[vendorIndex] : undefined;

    // Real shops only: the synthetic 'all' entry is the header row instead.
    const locations = useMemo(
        () => locationsData?.locations?.filter((location) => location._id !== 'all') ?? [],
        [locationsData],
    );

    const [draft, setDraft] = useState<VendorDraft>(() => buildDraft(vendor, locations));

    // A refetch can replace the vendor object; re-seed so the form shows what
    // was saved rather than a stale copy. The locations are what the stored
    // preferences resolve against, so a late load has to re-seed too.
    useEffect(() => {
        setDraft(buildDraft(vendor, locations));
    }, [vendor, locations]);

    // An out-of-range index has nothing to edit, so fall back to the list.
    useEffect(() => {
        if (!isAddMode && !vendor) navigate('/settings/vendors', { replace: true });
    }, [isAddMode, vendor, navigate]);

    useSettingsDetailCrumbs([{ label: isAddMode ? 'Add vendor' : draft.name || 'Vendor' }]);

    const closeFlow = () => navigate('/settings/vendors');

    const hasVendor = draft.name.trim().length > 0;

    const handleSelectSupplier = (supplier: SupplierSearchResult) => {
        setDraft((prev) => ({
            ...prev,
            name: supplier.title,
            supplierId: supplier._id,
            formattedAddress: supplier.formattedAddress,
            isDealer: supplier.isDealer,
            makes: supplier.isDealer ? prev.makes : 'all',
            nonDrpFees: supplier.isDealer ? prev.nonDrpFees : '',
            preferences: supplier.isDealer
                ? prev.preferences
                : withoutDealerPreferences(prev.preferences),
        }));
    };

    const handleClearSupplier = () => {
        setDraft((prev) => ({
            ...prev,
            name: '',
            supplierId: '',
            formattedAddress: undefined,
            isDealer: false,
            makes: 'all',
            nonDrpFees: '',
            preferences: withoutDealerPreferences(prev.preferences),
        }));
    };

    const handleSave = () => {
        if (!hasVendor) return;

        const nextMakes = draft.isDealer ? draft.makes : 'all';
        // Every row but this one: the same vendor may hold several rows, so
        // only the row being edited is exempt, not its siblings.
        const duplicate = vendors.find(
            (candidate, index) =>
                index !== vendorIndex &&
                candidate.name.toLowerCase() === draft.name.trim().toLowerCase() &&
                makesOverlap(candidate.makes, nextMakes),
        );
        if (duplicate) {
            toast({
                title: 'Vendor already added',
                description: `${duplicate.name} is already in your vendor list with overlapping makes.`,
                status: 'error',
                duration: 4000,
                isClosable: true,
            });
            return;
        }

        const parsedDiscount = draft.discount ? parseFloat(draft.discount) / 100 : undefined;
        const vendorData: OrgVendor = {
            supplierId: draft.supplierId || vendor?.supplierId || '',
            name: draft.name.trim(),
            formattedAddress: draft.formattedAddress,
            discount: parsedDiscount,
            directOrdering: draft.directOrdering,
            isDealer: draft.isDealer,
            makes: draft.isDealer ? toMakesPayload(draft.makes, oemOptions) : 'all',
            ptFee: draft.isDealer ? textToPercent(draft.nonDrpFees) : undefined,
            marginBump: textToPercent(draft.rebate),
            requiredLocations: draft.isDealer
                ? toLocationPayload(draft.preferences.required, locations)
                : [],
            superPreferredLocations: draft.isDealer
                ? toLocationPayload(draft.preferences.superPreferred, locations)
                : [],
            preferredLocations: toLocationPayload(draft.preferences.preferred, locations),
            avoidLocations: toLocationPayload(draft.preferences.avoid, locations),
            prohibitedLocations: toLocationPayload(draft.preferences.prohibited, locations),
        };

        const nextVendors = vendor
            ? vendors.map((candidate, index) => (index === vendorIndex ? vendorData : candidate))
            : [...vendors, vendorData];

        updateSettings.mutate(
            { vendors: nextVendors },
            {
                onSuccess: (response) => {
                    toast({
                        title: isAddMode ? 'Vendor added' : 'Vendor updated',
                        description: `${vendorData.name} has been saved.`,
                        status: 'success',
                        duration: 3000,
                        isClosable: true,
                    });
                    refetch();
                    // The server asks for an acknowledgement when a vendor is
                    // prohibited at a DRP location; the flow stays open for it.
                    if (response.data?.showProhibitedPopup) setShowProhibitedPopup(true);
                    else closeFlow();
                },
                onError: (error) => {
                    toast({
                        title: isAddMode ? 'Could not add vendor' : 'Could not update vendor',
                        description: error.message || 'Please try again.',
                        status: 'error',
                        duration: 5000,
                        isClosable: true,
                    });
                },
            },
        );
    };

    const handleRemove = () => {
        if (!vendor) return;
        updateSettings.mutate(
            {
                vendors: vendors.filter((_, index) => index !== vendorIndex),
            },
            {
                onSuccess: () => {
                    toast({
                        title: 'Vendor removed',
                        description: `${vendor.name} has been removed.`,
                        status: 'success',
                        duration: 3000,
                        isClosable: true,
                    });
                    refetch();
                    closeFlow();
                },
                onError: (error) => {
                    toast({
                        title: 'Could not remove vendor',
                        description: error.message || 'Please try again.',
                        status: 'error',
                        duration: 5000,
                        isClosable: true,
                    });
                },
            },
        );
    };

    if (!isAddMode && !vendor) return null;

    const left = (
        <VStack align="stretch" spacing={6}>
            {/* Step 1: the vendor, and nothing else. Everything below is that
                vendor's terms, so it only appears once one is chosen. */}
            {hasVendor ? (
                <SettingsCard title="Vendor">
                    <SettingsRow
                        label={draft.name}
                        description={draft.formattedAddress}
                        control={
                            draft.isDealer || isAddMode ? (
                                <HStack spacing={2}>
                                    {draft.isDealer && (
                                        <Badge
                                            colorScheme="partbay-blue"
                                            variant="subtle"
                                            fontSize="xs"
                                            fontWeight="medium"
                                            textTransform="none"
                                        >
                                            Dealer
                                        </Badge>
                                    )}
                                    {isAddMode && (
                                        <Button
                                            size="xs"
                                            variant="ghost"
                                            colorScheme="gray"
                                            onClick={handleClearSupplier}
                                        >
                                            Change
                                        </Button>
                                    )}
                                </HStack>
                            ) : undefined
                        }
                    />
                </SettingsCard>
            ) : (
                // Not a SettingsCard: the card clips its content, which would
                // cut off the search results hanging below the input.
                <Box>
                    <Text fontSize="sm" fontWeight="medium" color="gray.800" mb={1}>
                        Vendor name
                    </Text>
                    <Text fontSize="sm" color="gray.500" mb={2.5}>
                        Search the suppliers Yaxle knows and pick one.
                    </Text>
                    <VendorSearchField onSelect={handleSelectSupplier} autoFocus />
                    <HStack spacing={2} align="flex-start" mt={4}>
                        <Icon
                            as={TbInfoCircle as React.ElementType}
                            boxSize={4}
                            color="gray.400"
                            mt="2px"
                            flexShrink={0}
                        />
                        <Text fontSize="sm" color="gray.500">
                            Don't see the vendor you're looking for? That means we haven't interacted
                            with them yet. If you work with them often, check back in a few days and
                            they'll appear.
                        </Text>
                    </HStack>
                </Box>
            )}

            {/* Step 2: the vendor's terms. */}
            {hasVendor && (
                <SettingsCard
                    title="Terms"
                    description="How Yaxle treats this vendor's pricing."
                >
                    {draft.isDealer && (
                        <SettingsRow
                            label="Makes"
                            description="Which makes these terms apply to."
                            control={
                                <VendorMakesSelect
                                    value={draft.makes}
                                    options={oemOptions.map((option) => ({
                                        _id: option._id,
                                        name: option.name,
                                    }))}
                                    onChange={(makes) => setDraft((prev) => ({ ...prev, makes }))}
                                />
                            }
                        />
                    )}
                    <SettingsRow
                        label="Discount"
                        description="Percentage discount this vendor offers off list."
                        control={
                            <VendorPercentInput
                                value={draft.discount}
                                onChange={(discount) => setDraft((prev) => ({ ...prev, discount }))}
                            />
                        }
                    />
                    {/* <SettingsRow
                        label="Rebate"
                        description="Percentage of order volume received from vendor-specific rebate"
                        control={
                            <VendorPercentInput
                                value={draft.rebate}
                                onChange={(rebate) => setDraft((prev) => ({ ...prev, rebate }))}
                            />
                        }
                    /> */}
                    {draft.isDealer && (
                        <SettingsRow
                            label="Non-DRP fees"
                            description="Fee that vendors pass through on non-DRP jobs for the use of a marketplace."
                            control={
                                <VendorPercentInput
                                    value={draft.nonDrpFees}
                                    onChange={(nonDrpFees) =>
                                        setDraft((prev) => ({ ...prev, nonDrpFees }))
                                    }
                                />
                            }
                        />
                    )}
                    <SettingsRow
                        label="Direct ordering"
                        description="Place orders with this vendor without seeing the quote first."
                        control={
                            <SegmentedControl<DirectOrderingChoice>
                                aria-label="Direct ordering"
                                segments={DIRECT_ORDERING_OPTIONS}
                                value={draft.directOrdering ?? 'none'}
                                onChange={(value) =>
                                    setDraft((prev) => ({
                                        ...prev,
                                        directOrdering: value === 'none' ? null : value,
                                    }))
                                }
                            />
                        }
                    />
                </SettingsCard>
            )}
        </VStack>
    );

    return (
        <>
            <SettingsFlow
                steps={[{ id: 'details', label: isAddMode ? 'Add vendor' : 'Vendor' }]}
                activeStepId="details"
                onCancel={closeFlow}
                onSave={handleSave}
                saveLabel={isAddMode ? 'Add Vendor' : 'Save Vendor'}
                isSaveDisabled={!hasVendor}
                isSaving={updateSettings.isPending}
                actions={
                    vendor ? (
                        <Button
                            size="sm"
                            variant="outline"
                            colorScheme="red"
                            onClick={handleRemove}
                            isDisabled={updateSettings.isPending}
                        >
                            Remove Vendor
                        </Button>
                    ) : undefined
                }
            >
                <SettingsFlowSplit
                    leftWidth="40%"
                    leftTitle={isAddMode ? 'Add vendor' : 'Vendor details'}
                    left={left}
                    // Step 3: the preferences table only exists once there is a
                    // vendor to set them for; before that the pane stays empty.
                    rightTitle={hasVendor ? 'Preferences by location' : undefined}
                    rightDescription={
                        hasVendor ? 'A location can only belong to one category at a time.' : undefined
                    }
                    right={
                        hasVendor ? (
                            <Flex direction="column" flex={1} minH={0} px={6} pb={6}>
                                <VendorPreferencesTable
                                    locations={locations}
                                    preferences={draft.preferences}
                                    onChange={(preferences) =>
                                        setDraft((prev) => ({ ...prev, preferences }))
                                    }
                                    disabledKeys={
                                        draft.isDealer ? undefined : [...DEALER_ONLY_PREFERENCES]
                                    }
                                    disabledReason="Only dealer vendors can be required or super preferred."
                                />
                            </Flex>
                        ) : null
                    }
                    isRightFlush
                />
            </SettingsFlow>

            <ProhibitedVendorAlertDialog
                isOpen={showProhibitedPopup}
                leastDestructiveRef={prohibitedConfirmRef}
                onConfirm={() => {
                    setShowProhibitedPopup(false);
                    closeFlow();
                }}
            />
        </>
    );
};

export default VendorDetailSection;
